import type { LocalUserChoices } from '@livekit/components-react'
import { createLocalVideoTrack, type LocalVideoTrack, VideoPresets } from 'livekit-client'
import { useEffect, useRef, useState } from 'react'
import logo from './assets/everhall-logo.svg'
import {
  loadSpeakerChoice,
  playTestTone,
  saveSpeakerChoice,
  supportsAudioOutputSelection,
} from './audioOutput'
import { CapabilityRetry } from './CapabilityRetry'
import {
  classifyDeviceError,
  createLevelMeter,
  type LevelMeter,
  type MicAccess,
  needsHelp,
  probeCamera,
  probeMic,
  setLastCamAccess,
  setLastMicAccess,
  stopStream,
} from './deviceProbe'
import { listDevices, loadDeviceChoice, saveDeviceChoice } from './devices'
import { report } from './diagnostics'
import { isMeeting, isRTL, locale, t } from './i18n'
import { MicHelpDialog } from './MicHelpDialog'
import { hostJoinWarning } from './prejoinGate'
import type { PrejoinData } from './types'

/**
 * Replaces LiveKit's PreJoin prefab.
 *
 * The prefab asks the user to type a display name, which is wrong here twice
 * over: the API already knows exactly who holds this join code (the code is
 * bound to a user id), and a free-text name would let a student appear in a
 * child's class under any label they like. The authoritative name comes from the
 * token the server mints, so this screen only ever *shows* the identity.
 *
 * What it does own is the part the user genuinely decides: which camera and
 * microphone, and whether to start with either switched off.
 */

interface Props {
  context: PrejoinData
  busy: boolean
  onJoin: (choices: LocalUserChoices) => void
  /** Only used to attribute the microphone-readiness telemetry to this join. */
  credential: { code?: string | null; reconnectToken?: string | null }
}

/**
 * Peak level that counts as "we heard you". Named because the on-screen verdict
 * and the telemetry MUST use the same number — a dashboard counting silent joins
 * against a different threshold from the one the child was shown would be
 * measuring a thing nobody experienced.
 */
const HEARD_PEAK = 0.12

export function Prejoin({ context, busy, onJoin, credential }: Props) {
  const { current_user: user, session } = context
  // A Ghost holds a subscribe-only token: it can neither publish nor be seen.
  // So the whole device half of this screen is skipped — no preview, no pickers,
  // no toggles — and there is nothing to acquire a camera or microphone for.
  const isGhost = user.meetingRole === 'Ghost'
  // teachers only. The prejoin gate applies to Hosts; students, parents and Ghosts
  // are never held back over their own hardware — see prejoinGate.ts.
  const isHost = user.meetingRole === 'Host'
  // A parent's camera starts OFF: they're stepping into a child's classroom to
  // talk to the teacher, and turning their own video on should be a choice
  // they make, not a default that surprises them. Mic stays on — the
  // conversation is the point. Students and teachers keep camera-on defaults.
  const [camEnabled, setCamEnabled] = useState(user.appRole !== 'Parent')
  const [micEnabled, setMicEnabled] = useState(true)
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])
  const [mics, setMics] = useState<MediaDeviceInfo[]>([])
  const [camId, setCamId] = useState<string>(() => loadDeviceChoice('videoinput'))
  const [micId, setMicId] = useState<string>(() => loadDeviceChoice('audioinput'))
  /**
   * The device the browser ACTUALLY opened, as opposed to the one the user
   * picked. Empty until a probe succeeds.
   *
   * Needed because `camId`/`micId` start empty — "no preference" — and an empty
   * `value` matches no `<option>`, so the select fell back to displaying the
   * FIRST enumerated device. On Windows that is rarely the device in use: the
   * list is driver order while capture follows the OS default, so the label
   * routinely named a mic that was not live. A teacher debugging "wrong
   * microphone" was then changing a dropdown that had never described reality.
   *
   * Kept OUT of `camId`/`micId` on purpose — those are effect dependencies, so
   * writing the resolved id back into them would re-run the probe and re-open
   * the device for a value the user never chose.
   */
  const [liveCamId, setLiveCamId] = useState<string>('')
  const [liveMicId, setLiveMicId] = useState<string>('')
  /**
   * Speakers, and the chosen one. Only ever populated where the browser can
   * actually redirect playback (Chromium), so the control is absent rather than
   * decorative everywhere else — see audioOutput.ts.
   */
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([])
  const [speakerId, setSpeakerId] = useState<string>(loadSpeakerChoice)
  const [testingSpeaker, setTestingSpeaker] = useState(false)
  const [permissionDenied, setPermissionDenied] = useState(false)
  // Bumped by "Try again" to re-run the preview after the user allows access —
  // in place, without a reload. Reloading would lose the (already-scrubbed) join
  // code and strand the student on "No class link".
  const [previewNonce, setPreviewNonce] = useState(0)

  const videoRef = useRef<HTMLVideoElement>(null)
  const trackRef = useRef<LocalVideoTrack | null>(null)

  // The microphone gets its OWN probe, independent of the camera.
  //
  // Before this, the only device request here was for the camera, and only when
  // the camera was enabled — which is off by default on a rejoin. So a student
  // could arrive at a lesson having proven nothing at all, with the mic toggle
  // cheerfully reading "on", and discover only from a teacher's confusion twenty
  // minutes later that the browser had never given us a microphone. `micStatus`
  // is what the toggle should have meant all along.
  const [micStatus, setMicStatus] = useState<MicAccess | 'checking' | 'idle'>('checking')
  const [micErrorName, setMicErrorName] = useState<string | undefined>()
  const [showMicHelp, setShowMicHelp] = useState(false)
  const [showCamHelp, setShowCamHelp] = useState(false)
  const [micProbeNonce, setMicProbeNonce] = useState(0)
  // Peak level seen since the probe started, so "we heard you" latches instead of
  // flickering off between syllables.
  const [micPeak, setMicPeak] = useState(0)
  const [micLevel, setMicLevel] = useState(0)
  const micStreamRef = useRef<MediaStream | undefined>(undefined)
  const micSelectRef = useRef<HTMLSelectElement>(null)
  const micMeterRef = useRef<LevelMeter | null>(null)
  const micFrameRef = useRef(0)
  /**
   * Bumped to invalidate an in-flight probe. A generation counter rather than a
   * closure flag because `submit` has to be able to cancel a probe it did not
   * start — otherwise a late getUserMedia resolution re-opens the microphone
   * milliseconds after the room asked for it.
   */
  const micGenRef = useRef(0)
  /** Last state we reported, so a re-render cannot re-send the same datapoint. */
  const micReportedRef = useRef<string | null>(null)
  /**
   * Camera access, classified the same way as the microphone.
   *
   * The old code only knew a boolean `permissionDenied` derived from the camera
   * preview, which could not tell "blocked" from "this device has no camera" —
   * and those need OPPOSITE responses. A blocked camera is worth a walkthrough; a
   * missing one is just a fact about the device, and a lesson with working audio
   * is a perfectly good lesson. Conflating them is how a class ends up on another calling app
   * over a webcam that was never going to exist.
   */
  const [camStatus, setCamStatus] = useState<MicAccess | 'checking' | 'idle'>('idle')
  const [camErrorName, setCamErrorName] = useState<string | undefined>()
  const camReportedRef = useRef<string | null>(null)

  // Live preview so nobody joins a class pointing at the ceiling. Also the
  // moment the browser asks for permission, which is why the denial state is
  // handled here rather than being discovered inside the room.
  useEffect(() => {
    // A Ghost never opens a device — starting the preview here would fire a
    // permission prompt and try to publish a camera the token cannot carry.
    if (isGhost) return
    let cancelled = false

    async function start() {
      try {
        // Same cap as the room (room-options.ts). Without a resolution the SDK
        // captures 720p for a thumbnail preview, then stops it and captures
        // again for real — twice the startup cost on the weakest devices.
        const track = await createLocalVideoTrack({
          deviceId: camId || undefined,
          resolution: VideoPresets.h360.resolution,
        })
        if (cancelled) {
          track.stop()
          return
        }
        trackRef.current = track
        if (videoRef.current) track.attach(videoRef.current)
        setPermissionDenied(false)
        setCamStatus('ok')
        setCamErrorName(undefined)
        setLastCamAccess('ok')
        // Which camera did we actually get? See liveCamId.
        setLiveCamId(track.mediaStreamTrack?.getSettings().deviceId ?? '')

        const devices = await listDevices()
        if (cancelled) return
        setCameras(devices.videoinput)
        setMics(devices.audioinput)
      } catch (error) {
        if (cancelled) return
        // Classify the error we ALREADY have rather than asking for the camera a
        // second time. A re-probe could fire a second permission prompt at a child
        // mid-prejoin, and — worse — if the preview failed for a transient track
        // reason while the re-probe succeeded, we would end up advertising
        // "blocked" for a camera that works while having just stopped the stream
        // that proved it.
        const classified = classifyDeviceError(error)
        let access = classified.access
        setCamErrorName(classified.errorName)
        // Only when the name tells us nothing is a real probe worth its prompt.
        if (access === 'unknown') {
          const probe = await probeCamera(camId || undefined)
          stopStream(probe.stream)
          if (cancelled) return
          access = probe.access
        }
        setCamStatus(access)
        setLastCamAccess(access)
        // "No camera on this device" is not a denial, and showing the blocked
        // warning for it is what made a missing webcam look like a broken class.
        setPermissionDenied(access !== 'missing')
      }
    }

    if (camEnabled) {
      void start()
    }

    return () => {
      cancelled = true
      trackRef.current?.stop()
      trackRef.current = null
    }
  }, [isGhost, camEnabled, camId, previewNonce])

  useEffect(() => {
    if (camStatus === 'checking' || camStatus === 'idle') return
    const key = `${camStatus}:${camErrorName ?? ''}`
    if (camReportedRef.current === key) return
    camReportedRef.current = key
    report(credential, {
      stage: 'permissions',
      outcome: camStatus === 'ok' || camStatus === 'missing' ? 'ok' : 'error',
      errorKind: camErrorName,
      permission: `camera=${camStatus}`,
    })
  }, [camStatus, camErrorName, credential])

  // Probe the microphone and meter it live, so the child can SEE that they are
  // audible rather than trusting an icon.
  //
  // A permission check alone is not enough and that is the whole point: a muted
  // headset switch, an OS-level mute or the wrong input device all yield a
  // perfectly live track of silence, which is very likely what actually happened
  // on 2026-08-02. Prompting speech and watching the level is the only honest
  // test, and the prejoin is the one place it costs nothing to ask.
  useEffect(() => {
    // A Ghost publishes nothing, and a student who deliberately turned the mic off
    // is not waiting to be heard. Neither is a state worth probing, reporting or
    // advertising — `idle` says "we did not ask", which is different from "ok".
    if (isGhost || !micEnabled) {
      setMicStatus('idle')
      return
    }

    // Every async step below re-checks this. A stale probe that resolves after the
    // effect was torn down must not re-open the device, or it becomes the `busy`
    // it was sent to detect.
    micGenRef.current += 1
    const gen = micGenRef.current
    const live = () => micGenRef.current === gen

    async function run() {
      setMicStatus('checking')
      setMicPeak(0)
      setMicLevel(0)

      const result = await probeMic(micId || undefined)
      if (!live()) {
        stopStream(result.stream)
        return
      }

      setMicStatus(result.access)
      setMicErrorName(result.errorName)
      // Carried into the room so the teacher sees a reason, not a mute icon.
      setLastMicAccess(result.access)
      // Only offer the walkthrough for states a child can act on. `unknown` stays
      // quiet: we do not understand it, so we must not lecture.
      if (needsHelp(result.access)) setShowMicHelp(true)

      if (result.access !== 'ok' || !result.stream) {
        stopStream(result.stream)
        return
      }

      // Own the stream only while still current, so cleanup can always find it.
      micStreamRef.current = result.stream
      // Which microphone did we actually get? See liveMicId.
      setLiveMicId(result.stream.getAudioTracks()[0]?.getSettings().deviceId ?? '')

      // Device labels are readable only once permission has been granted, which is
      // why the mic list is filled HERE rather than as a side effect of the camera
      // path succeeding.
      try {
        const devices = await listDevices()
        if (live()) setMics(devices.audioinput)
        // Speakers ride the same permission: before a grant, Chromium returns
        // outputs with empty labels, and "Speaker, Speaker, Speaker" is a menu
        // nobody can use. Listing them here means the labels are real.
        if (live() && supportsAudioOutputSelection()) {
          setSpeakers(devices.audiooutput.filter((device) => device.label))
        }
      } catch {
        /* labels are a nicety, not a requirement */
      }
      // enumerateDevices was a second await: re-check before building a meter, or
      // an AudioContext is created after teardown and never closed.
      if (!live()) {
        stopStream(result.stream)
        micStreamRef.current = undefined
        return
      }

      const meter = createLevelMeter(result.stream)
      if (!meter) return
      micMeterRef.current = meter
      const tick = () => {
        if (!live()) return
        const value = meter.level()
        setMicLevel(value)
        setMicPeak((peak) => (value > peak ? value : peak))
        micFrameRef.current = window.requestAnimationFrame(tick)
      }
      micFrameRef.current = window.requestAnimationFrame(tick)
    }

    void run()

    return releaseMicProbe
  }, [isGhost, micEnabled, micId, micProbeNonce])

  useEffect(() => {
    const permissions = navigator.permissions
    if (!permissions?.query) return
    let cancelled = false
    const detaches: Array<() => void> = []
    const watch = (name: PermissionName, onGranted: () => void) => {
      void permissions
        .query({ name })
        .then((result) => {
          const onChange = () => {
            if (result.state === 'granted') onGranted()
          }
          if (cancelled) return
          result.addEventListener('change', onChange)
          detaches.push(() => result.removeEventListener('change', onChange))
        })
        .catch(() => undefined)
    }
    watch('microphone' as PermissionName, () => setMicProbeNonce((n) => n + 1))
    watch('camera' as PermissionName, () => setPreviewNonce((n) => n + 1))
    return () => {
      cancelled = true
      for (const off of detaches) off()
    }
  }, [])

  // Report what the browser actually gave us. This is the signal that was missing
  // on 2026-08-02: with it, "students cannot be heard" is a number on a dashboard
  // instead of a teacher's guess after twenty minutes.
  useEffect(() => {
    if (micStatus === 'checking' || micStatus === 'idle') return
    // De-duped: this effect also re-runs when `credential` is re-created, and a
    // repeated datapoint would inflate the very rate we want to trust.
    const key = `${micStatus}:${micErrorName ?? ''}`
    if (micReportedRef.current === key) return
    micReportedRef.current = key
    report(credential, {
      stage: 'permissions',
      outcome: micStatus === 'ok' ? 'ok' : 'error',
      errorKind: micErrorName,
      permission: `mic=${micStatus}`,
    })
  }, [micStatus, micErrorName, credential])

  /**
   * Give the microphone back, synchronously.
   *
   * MUST run before the room acquires its own track. Relying on effect-cleanup
   * ordering was a real bug: JoinPage keeps Prejoin mounted while `busy` is true,
   * so the probe stream was still held when LiveKit called getUserMedia — and a
   * second acquisition fails with NotReadableError on iOS Safari and much of
   * Android. The prejoin would have manufactured the exact "microphone is busy"
   * it exists to detect.
   */
  const releaseMicProbe = () => {
    micGenRef.current += 1
    if (micFrameRef.current) window.cancelAnimationFrame(micFrameRef.current)
    micFrameRef.current = 0
    micMeterRef.current?.stop()
    micMeterRef.current = null
    stopStream(micStreamRef.current)
    micStreamRef.current = undefined
  }

  const retryMic = () => {
    setMicProbeNonce((n) => n + 1)
    setShowMicHelp(false)
  }

  // Re-attempt the camera after the user grants permission at the browser level.
  const retryPreview = () => {
    setPermissionDenied(false) // optimistic; start() sets it back if it fails again
    setCamEnabled(true)
    setPreviewNonce((n) => n + 1)
  }
  const retryCamera = () => {
    retryPreview()
    setShowCamHelp(false)
  }

  const hostWarning = hostJoinWarning({
    isHost,
    isGhost,
    micEnabled,
    micStatus,
    micPeak,
    heardPeak: HEARD_PEAK,
  })

  const submit = () => {
    if (isHost && hostWarning) {
      report(credential, {
        stage: 'permissions',
        outcome: 'error',
        errorKind: 'host_join_anyway',
        permission: `host_join_anyway=${hostWarning},mic=${micStatus},peak=${micPeak.toFixed(3)}`,
      })
    }
    // "Joined anyway with a microphone we never heard" — counted HERE, at the
    // one moment the verdict is final.
    //
    // The permissions report above says `mic=ok` for this student, because the
    // browser did hand us a working capture, and that is the honest answer to the
    // question it asks. But a live track we never heard a sound through is the
    // 2026-08-02 failure exactly, and it was invisible in the funnel: the quiet
    // hint was shown on screen and nowhere else, so nobody could count how often
    // children start lessons inaudible. Reported at Join rather than from the
    // meter effect because the peak only settles once, and a rAF-driven value in
    // an effect dependency would report on every frame.
    if (!isGhost && micEnabled && micStatus === 'ok' && micPeak <= HEARD_PEAK) {
      report(credential, {
        stage: 'permissions',
        outcome: 'error',
        errorKind: 'mic_never_heard',
        permission: `mic=ok,quiet=1,peak=${micPeak.toFixed(3)}`,
      })
    }

    // Stop the preview before handing over: leaving it running holds the camera
    // and the room would open with a device already in use.
    trackRef.current?.stop()
    trackRef.current = null
    // Same rule for the microphone, and it has to happen HERE rather than in the
    // effect cleanup: JoinPage keeps this screen mounted while `busy` is true, so
    // cleanup has not run by the time the room calls getUserMedia. A second
    // acquisition while we still hold the device fails with NotReadableError on
    // iOS Safari and much of Android — the prejoin manufacturing the very
    // "microphone is busy" it exists to detect.
    releaseMicProbe()
    onJoin({
      // Forced off for a Ghost regardless of any saved choice: publishing is
      // impossible with a subscribe-only token, so attempting it would only
      // produce a doomed device request inside the room.
      videoEnabled: isGhost ? false : camEnabled,
      audioEnabled: isGhost ? false : micEnabled,
      videoDeviceId: isGhost ? '' : camId,
      audioDeviceId: isGhost ? '' : micId,
      username: user.name,
    } as LocalUserChoices)
  }

  const fixHostMic = () => {
    if (hostWarning === 'fault') setShowMicHelp(true)
    else if (hostWarning === 'off') setMicEnabled(true)
    else if (hostWarning === 'unheard') {
      const details = micSelectRef.current?.closest('details')
      if (details) details.open = true
      micSelectRef.current?.focus()
    }
  }

  const initials = user.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')

  // Three-way, not two: a Parent joins as a plain Participant like a student, so
  // without this branch the "not a Student" fallthrough would label them Teacher.
  const roleKey =
    user.appRole === 'Student'
      ? 'role.student'
      : user.appRole === 'Parent'
        ? 'role.parent'
        : 'role.teacher'

  return (
    <div className="prejoin" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="prejoin__brand">
        <img src={logo} alt="Everhall" height={34} />
      </header>

      {isGhost ? (
        <div className="prejoin__card prejoin__card--observer">
          <div className="prejoin__side">
            {/* Identity is presented, never entered — same as the full card. */}
            <div className="identity">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="identity__avatar" />
              ) : (
                <span className="identity__avatar identity__avatar--initials">{initials}</span>
              )}
              <div>
                <p className="identity__name">{user.name}</p>
              </div>
            </div>

            <p className="prejoin__observer-note">{t('prejoin.ghost')}</p>

            <div className="class-meta">
              <p className="class-meta__label">{t('prejoin.classLabel')}</p>
              <p className="class-meta__value">{session.title}</p>
              <p className="class-meta__value">
                {session.type === '1:1' ? t('prejoin.solo') : t('prejoin.group')}
              </p>
              <p className="class-meta__time">{formatWindow(session.start_at, session.end_at)}</p>
            </div>

            <button
              type="button"
              className="primary-button primary-button--lg"
              onClick={submit}
              disabled={busy}
            >
              {busy ? t('prejoin.joining') : t('prejoin.join')}
            </button>
          </div>
        </div>
      ) : (
        <div className="prejoin__card">
          <div className="prejoin__preview">
            {camEnabled && !permissionDenied ? (
              <video ref={videoRef} autoPlay playsInline muted className="prejoin__video" />
            ) : (
              <div className="prejoin__avatar-stage">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="prejoin__avatar" />
                ) : (
                  <span className="prejoin__initials">{initials}</span>
                )}
                <p className="prejoin__camera-off">
                  {permissionDenied ? t('prejoin.blocked') : t('prejoin.cameraOff')}
                </p>
              </div>
            )}

            <div className="prejoin__toggles">
              <button
                type="button"
                className={`icon-toggle ${micEnabled ? '' : 'icon-toggle--off'}`}
                onClick={() => setMicEnabled((v) => !v)}
                aria-pressed={micEnabled}
                aria-label={t(micEnabled ? 'prejoin.micOn' : 'prejoin.micOff')}
                // Native title, NOT data-tip: the preview crops overflow, so a
                // CSS tooltip above these toggles would be clipped.
                title={t(micEnabled ? 'prejoin.micOn' : 'prejoin.micOff')}
              >
                {micEnabled ? <MicIcon /> : <MicOffIcon />}
              </button>
              <button
                type="button"
                className={`icon-toggle ${camEnabled ? '' : 'icon-toggle--off'}`}
                onClick={() => setCamEnabled((v) => !v)}
                aria-pressed={camEnabled}
                aria-label={t(camEnabled ? 'prejoin.camOn' : 'prejoin.camOff')}
                title={t(camEnabled ? 'prejoin.camOn' : 'prejoin.camOff')}
              >
                {camEnabled ? <CamIcon /> : <CamOffIcon />}
              </button>
            </div>
          </div>

          <div className="prejoin__side">
            {/* Identity is presented, never entered. */}
            <div className="identity">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="identity__avatar" />
              ) : (
                <span className="identity__avatar identity__avatar--initials">{initials}</span>
              )}
              <div>
                <p className="identity__name">{user.name}</p>
                <p className="identity__role">{t(roleKey)}</p>
              </div>
            </div>

            <div className="class-meta">
              <p className="class-meta__label">{t('prejoin.classLabel')}</p>
              <p className="class-meta__value">{session.title}</p>
              <p className="class-meta__value">
                {session.type === '1:1' ? t('prejoin.solo') : t('prejoin.group')}
              </p>
              <p className="class-meta__time">{formatWindow(session.start_at, session.end_at)}</p>
            </div>

            {camStatus === 'missing' ? (
              /* Not a warning. This device simply has no camera, which is common on
               a desktop and entirely compatible with a good lesson — the teacher
               can still hear them. Presenting it as an error taught children (and
               teachers) that the class was broken, which is how a webcam that never
               existed ends up being "fixed" by moving to another calling app. */
              <div className="notice">
                <p>{t('prejoin.noCamera')}</p>
              </div>
            ) : permissionDenied ? (
              <div className="notice notice--warn">
                <p>{t('prejoin.blockedHelp')}</p>
                {camStatus === 'blocked' ? (
                  <CapabilityRetry
                    kind="cam"
                    label={t('prejoin.retry')}
                    onReady={retryPreview}
                    className="notice__retry"
                  />
                ) : (
                  <button type="button" className="notice__retry" onClick={retryPreview}>
                    {t('prejoin.retry')}
                  </button>
                )}
                {camStatus === 'blocked' || camStatus === 'busy' ? (
                  <button
                    type="button"
                    className="notice__retry"
                    onClick={() => setShowCamHelp(true)}
                  >
                    {t('prejoin.fixCamera')}
                  </button>
                ) : null}
              </div>
            ) : (
              <details className="prejoin__devices" open={!isMeeting()}>
                <summary hidden={!isMeeting()}>{t('devices.heading')}</summary>
                <div className="devices">
                  <label className="devices__row">
                    <span>{t('prejoin.camera')}</span>
                    <select
                      value={camId || liveCamId}
                      onChange={(e) => {
                        setCamId(e.target.value)
                        saveDeviceChoice('videoinput', e.target.value)
                      }}
                      disabled={!camEnabled || cameras.length === 0}
                    >
                      {cameras.length === 0 && <option value="">{t('prejoin.default')}</option>}
                      {cameras.map((d) => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || t('prejoin.camera')}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="devices__row">
                    <span>{t('prejoin.microphone')}</span>
                    <select
                      ref={micSelectRef}
                      value={micId || liveMicId}
                      onChange={(e) => {
                        setMicId(e.target.value)
                        saveDeviceChoice('audioinput', e.target.value)
                      }}
                      disabled={mics.length === 0}
                    >
                      {mics.length === 0 && <option value="">{t('prejoin.default')}</option>}
                      {mics.map((d) => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || t('prejoin.microphone')}
                        </option>
                      ))}
                    </select>
                  </label>
                  {/* The speaker row, and the reason it exists: "I can't hear the
                  teacher" used to have no answer inside the app, so a child
                  hunted through the microphone menu instead. Rendered only where
                  playback can actually be redirected, and paired with a test tone
                  — a picker without a way to check it just moves the guess. */}
                  {speakers.length > 0 ? (
                    <label className="devices__row">
                      <span>{t('prejoin.speaker')}</span>
                      <span className="devices__pair">
                        <select
                          value={speakerId}
                          onChange={(e) => {
                            setSpeakerId(e.target.value)
                            // Saved on change, not on Join: this screen is also where
                            // someone lands after a failed lesson, and the choice
                            // should survive them giving up and reloading.
                            saveSpeakerChoice(e.target.value)
                          }}
                        >
                          <option value="">{t('prejoin.default')}</option>
                          {speakers.map((d) => (
                            <option key={d.deviceId} value={d.deviceId}>
                              {d.label || t('prejoin.speaker')}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="devices__test"
                          disabled={testingSpeaker}
                          onClick={() => {
                            setTestingSpeaker(true)
                            void playTestTone(speakerId).finally(() => setTestingSpeaker(false))
                          }}
                        >
                          {t(testingSpeaker ? 'prejoin.speakerPlaying' : 'prejoin.speakerTest')}
                        </button>
                      </span>
                    </label>
                  ) : null}
                </div>
              </details>
            )}

            {/* The mic check. Deliberately placed above Join: it is the last thing
              a child sees before entering, and a moving bar is the only proof
              worth trusting. Never blocks Join — a broken microphone must not
              keep a student out of a paid lesson. */}
            {!isGhost && micEnabled ? (
              <div
                className={`mic-check ${needsHelp(micStatus as MicAccess) ? 'mic-check--bad' : ''}`}
                aria-live="polite"
              >
                {micStatus === 'checking' ? (
                  <p className="mic-check__line">{t('mic.check.checking')}</p>
                ) : needsHelp(micStatus as MicAccess) ? (
                  <>
                    <p className="mic-check__line mic-check__line--bad">
                      {micStatus === 'missing'
                        ? t('mic.status.missing')
                        : micStatus === 'busy'
                          ? t('mic.status.busy')
                          : // Unreachable today — `probeMic` classifies getUserMedia
                            // errors and no error means "silent". Spelled out anyway
                            // because `needsHelp` now admits it, so the day anything
                            // sets it here the fallthrough would confidently say
                            // "blocked" about a permission that was granted.
                            micStatus === 'silent'
                            ? t('mic.status.silent')
                            : t('mic.status.blocked')}
                    </p>
                    <button
                      type="button"
                      className="mic-check__fix"
                      onClick={() => setShowMicHelp(true)}
                    >
                      {t('mic.status.fix')}
                    </button>
                  </>
                ) : micStatus === 'ok' ? (
                  <>
                    <p className="mic-check__line">
                      {micPeak > HEARD_PEAK ? t('mic.check.heard') : t('mic.check.prompt')}
                    </p>
                    <div
                      className="mic-check__meter"
                      role="meter"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(micLevel * 100)}
                      aria-label={t('mic.check.title')}
                    >
                      <span
                        className="mic-check__fill"
                        style={{ width: `${Math.round(micLevel * 100)}%` }}
                      />
                    </div>
                    {/* Permission is granted and capture is live, yet nothing is
                      arriving — the exact silent failure a permission gate misses.
                      Offered as a hint, not an error: some children really are
                      quiet, and calling that broken would be its own bug. */}
                    {micPeak <= 0.02 ? (
                      <p className="mic-check__hint">{t('mic.check.quiet')}</p>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}

            {isHost && hostWarning ? (
              <div className="notice notice--warn" role="status" aria-live="polite">
                <p>
                  {t(
                    hostWarning === 'off'
                      ? 'prejoin.hostMicOff'
                      : hostWarning === 'checking'
                        ? 'prejoin.hostMicChecking'
                        : hostWarning === 'unheard'
                          ? 'prejoin.hostMicUnheard'
                          : 'prejoin.hostMicFault',
                  )}
                </p>
                {hostWarning !== 'checking' ? (
                  <button type="button" className="notice__retry" onClick={fixHostMic}>
                    {t('prejoin.fixMic')}
                  </button>
                ) : null}
              </div>
            ) : null}

            <button
              type="button"
              className="primary-button primary-button--lg"
              onClick={submit}
              disabled={busy}
            >
              {busy
                ? t('prejoin.joining')
                : hostWarning === 'fault' || hostWarning === 'off' || hostWarning === 'unheard'
                  ? t('prejoin.joinAnyway')
                  : t('prejoin.join')}
            </button>
          </div>
        </div>
      )}

      {showMicHelp && (needsHelp(micStatus as MicAccess) || micStatus === 'unknown') ? (
        <MicHelpDialog
          access={micStatus as MicAccess}
          tone="prejoin"
          onRetry={retryMic}
          onDismiss={() => setShowMicHelp(false)}
        />
      ) : null}
      {showCamHelp && (camStatus === 'blocked' || camStatus === 'busy') ? (
        <MicHelpDialog
          device="camera"
          access={camStatus}
          tone="prejoin"
          onRetry={retryCamera}
          onDismiss={() => setShowCamHelp(false)}
        />
      ) : null}
    </div>
  )
}

function formatWindow(startAt: string, endAt: string): string {
  try {
    const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
    const tag = locale === 'ar' ? 'ar-EG' : 'en-GB'
    return `${new Date(startAt).toLocaleTimeString(tag, opts)} – ${new Date(endAt).toLocaleTimeString(tag, opts)}`
  } catch {
    return ''
  }
}

/* Inline icons: the CSP forbids third-party scripts, and an icon font would be
   another request on a Cairo mobile connection for four glyphs. */
const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V22h2v-3.08A7 7 0 0 0 19 12h-2Z" />
  </svg>
)
const MicOffIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M15 10.6V6a3 3 0 0 0-5.94-.6l5.94 5.2ZM4.3 3 3 4.3l6 5.3V12a3 3 0 0 0 4.6 2.55l1.4 1.23A5 5 0 0 1 7 12H5a7 7 0 0 0 6 6.92V22h2v-3.08a7 7 0 0 0 3.2-1.35L19.7 20 21 18.7 4.3 3Z" />
  </svg>
)
const CamIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4Z" />
  </svg>
)
const CamOffIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M4.3 3 3 4.3 5.7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 .9-.6L19.7 21 21 19.7 4.3 3ZM17 8.5V7a1 1 0 0 0-1-1H9.5l7.5 7.4V13l4 4V6l-4 2.5Z" />
  </svg>
)
